import { useState, useEffect } from 'react';

export default function OverviewSection() {
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [stats, setStats] = useState({
    students: 0,
    tutors: 0,
    resources: 0,
    upcomingEvents: 0
  });
  
  const upcomingEvents = events.filter((e) => e.status === 'upcoming');

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch inquiries
      const inqRes = await fetch(`${API_BASE}/api/inquiries`, { headers });
      if (inqRes.ok) {
        const inqData = await inqRes.json();
        setInquiries(inqData);
      }
      
      // Fetch events
      const evRes = await fetch(`${API_BASE}/api/events`);
      if (evRes.ok) {
        const evData = await evRes.json();
        setEvents(evData);
      }

      // Fetch Stats
      const statsRes = await fetch(`${API_BASE}/api/users/stats`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openInquiries = inquiries.filter((i) => i.status === 'open');

  const statCards = [
    { label: 'Total Students', value: stats.students, icon: 'ri-user-line' },
    { label: 'Active Tutors', value: stats.tutors, icon: 'ri-user-star-line' },
    { label: 'Open Inquiries', value: openInquiries.length, icon: 'ri-mail-line' },
    { label: 'Resources', value: stats.resources, icon: 'ri-folder-open-line' },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Dashboard Overview</h2>
        <p className="text-slate-500 text-sm">Welcome back, Admin. Here&apos;s what&apos;s happening today.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-slate-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg">
                <i className={`${stat.icon} text-emerald-700 text-lg`}></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>{stat.value}</p>
            <p className="text-slate-500 text-xs mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent Inquiries */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800 text-sm">Recent Inquiries</h3>
            <span className="text-xs text-emerald-700 font-semibold cursor-pointer hover:underline">{openInquiries.length} open</span>
          </div>
          <div className="flex flex-col gap-3">
            {openInquiries.slice(0, 4).map((inq) => (
              <div key={inq._id} className="flex items-start gap-3 pb-3 border-b border-slate-50 last:border-0 last:pb-0">
                <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-full flex-shrink-0 text-xs font-bold text-slate-600">
                  {inq.name.split(' ').map((n: string) => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-700 truncate">{inq.name}</p>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{inq.subject}</p>
                </div>
              </div>
            ))}
            {openInquiries.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">No open inquiries</p>}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800 text-sm">Upcoming Events</h3>
            <span className="text-xs text-emerald-700 font-semibold cursor-pointer hover:underline">{upcomingEvents.length} scheduled</span>
          </div>
          <div className="flex flex-col gap-3">
            {upcomingEvents.slice(0, 4).map((ev) => (
              <div key={ev._id} className="flex items-center gap-3 pb-3 border-b border-slate-50 last:border-0 last:pb-0">
                <div className="w-10 h-10 flex-shrink-0 flex flex-col items-center justify-center bg-emerald-700 rounded-lg text-white">
                  <span className="text-sm font-bold leading-none">{ev.date.split('-')[2].split('T')[0]}</span>
                  <span className="text-xs opacity-80">{new Date(ev.date).toLocaleString('default', { month: 'short' })}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-700 truncate">{ev.title}</p>
                  <p className="text-xs text-slate-400">{ev.tutor} · {ev.time}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-semibold text-slate-700">{ev.booked}/{ev.spots}</p>
                  <p className="text-xs text-slate-400">booked</p>
                </div>
              </div>
            ))}
            {upcomingEvents.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">No upcoming events</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
